// beforeAfter.js
export class BeforeAfter {
    constructor(element) {
        this.element = element;
        this.BP = 768; // mobile / desktop breakpoint
        this.lastMode = null;
        this.dragging = false;
        this.animationId = null;

        // Store elements
        this.beforeImg = this.element.querySelector('img.before');
        this.afterImg = this.element.querySelector('img.after');

        // Create labels
        this.labelBefore = this.makeLabel('Pre', 'left');
        this.labelAfter = this.makeLabel('Posle', 'right');

        // Initialize
        this.init();
    }

    makeLabel(text, side) {
        const d = document.createElement('div');
        d.className = 'label ' + side;
        d.textContent = text;
        return d;
    }

    init() {
        this.setupEventListeners();
        this.run();
    }

    setupEventListeners() {
        window.addEventListener('resize', () => this.run());
    }

    buildMobile() {
        this.element.classList.add('slider-mode');
        this.element.classList.remove('static-mode');

        if (this.element.dataset.aspect) {
            this.element.style.aspectRatio = this.element.dataset.aspect.replace('x', ' / ');
        }

        // Reset image state
        this.beforeImg.style.clipPath = '';
        this.afterImg.style.clipPath = '';
        this.beforeImg.classList.remove('after-image', 'before-image');
        this.afterImg.classList.remove('after-image', 'before-image');

        // Clear container
        this.element.innerHTML = '';

        // Add images and labels
        this.element.append(this.afterImg, this.beforeImg);
        this.beforeImg.classList.add('before-image');

        // Add smooth transition
        this.beforeImg.style.transition = 'clip-path 0.2s ease-out';

        // Add labels
        this.element.appendChild(this.labelBefore);
        this.element.appendChild(this.labelAfter);

        // Add slider
        this.slider = document.createElement('div');
        this.slider.className = 'slider';
        this.slider.style.left = '50%';
        this.slider.style.transition = 'left 0.2s ease-out';
        this.beforeImg.style.clipPath = 'inset(0 50% 0 0)';
        this.element.appendChild(this.slider);

        // Setup drag events
        this.setupDragEvents();
    }

    setupDragEvents() {
        const handleMove = (clientX) => {
            if (!this.dragging) return;

            cancelAnimationFrame(this.animationId);
            this.animationId = requestAnimationFrame(() => {
                const r = this.element.getBoundingClientRect();
                const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * 100;

                // Apply smooth movement
                this.slider.style.left = p + '%';
                this.beforeImg.style.clipPath = `inset(0 ${100-p}% 0 0)`;
            });
        };

        // Mouse events
        this.slider.addEventListener('mousedown', () => {
            this.dragging = true;
            this.element.classList.add('dragging');
        });

        // Touch events
        this.slider.addEventListener('touchstart', (e) => {
            this.dragging = true;
            this.element.classList.add('dragging');
            e.preventDefault();
        }, { passive: false });

        const endDrag = () => {
            this.dragging = false;
            this.element.classList.remove('dragging');
            cancelAnimationFrame(this.animationId);
        };

        window.addEventListener('mouseup', endDrag);
        window.addEventListener('touchend', endDrag);

        window.addEventListener('mousemove', (e) => handleMove(e.clientX));
        window.addEventListener('touchmove', (e) => {
            if (e.touches[0]) handleMove(e.touches[0].clientX);
        }, { passive: false });

        // Add keyboard support for accessibility
        this.slider.setAttribute('tabindex', '0');
        this.slider.setAttribute('role', 'slider');
        this.slider.setAttribute('aria-valuemin', '0');
        this.slider.setAttribute('aria-valuemax', '100');
        this.slider.setAttribute('aria-valuenow', '50');

        this.slider.addEventListener('keydown', (e) => {
            const currentPos = parseFloat(this.slider.style.left || '50');
            let newPos;

            switch(e.key) {
                case 'ArrowLeft':
                    newPos = Math.max(0, currentPos - 5);
                    break;
                case 'ArrowRight':
                    newPos = Math.min(100, currentPos + 5);
                    break;
                case 'Home':
                    newPos = 0;
                    break;
                case 'End':
                    newPos = 100;
                    break;
                default:
                    return;
            }

            e.preventDefault();
            this.slider.style.left = newPos + '%';
            this.beforeImg.style.clipPath = `inset(0 ${100-newPos}% 0 0)`;
            this.slider.setAttribute('aria-valuenow', newPos.toString());
        });
    }

    buildDesktop() {
        this.element.classList.add('static-mode');
        this.element.classList.remove('slider-mode');
        this.element.style.aspectRatio = 'auto';

        // Reset clip paths and classes
        this.beforeImg.style.clipPath = '';
        this.afterImg.style.clipPath = '';
        this.beforeImg.classList.remove('before-image');
        this.afterImg.classList.remove('after-image');

        // Clear container
        this.element.innerHTML = '';

        // Create left/right containers
        const left = document.createElement('div');
        const right = document.createElement('div');
        left.className = right.className = 'half';

        // Append before/after
        left.append(this.labelBefore, this.beforeImg);
        right.append(this.labelAfter, this.afterImg);

        this.element.append(left, right);
    }

    run() {
        const mode = window.innerWidth < this.BP ? 'mobile' : 'desktop';
        if (mode === this.lastMode) return;
        this.lastMode = mode;

        this.element.innerHTML = '';
        mode === 'mobile' ? this.buildMobile() : this.buildDesktop();
    }
}

// Export the initialization function
export function initBeforeAfter() {
    document.querySelectorAll('.before-after').forEach(element => {
        new BeforeAfter(element);
    });
}